<script setup lang="ts">
import type { ListboxItemEmits, ListboxItemProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { CheckCircleIcon } from '@heroicons/vue/20/solid'
import { reactiveOmit } from '@vueuse/core'
import { injectListboxRootContext, ListboxItem, useForwardProps } from 'reka-ui'
import { computed } from 'vue'
import { cn } from '#ui-kit/lib/utils'

const props = defineProps<
  ListboxItemProps & {
    class?: HTMLAttributes['class']
    selected?: boolean
  }
>()
const emits = defineEmits<ListboxItemEmits>()

const delegatedProps = reactiveOmit(props, 'class', 'selected')

const forwarded = useForwardProps(delegatedProps)

const rootContext = injectListboxRootContext()
const isDisabled = computed(() => rootContext.disabled.value || props.disabled)

function onSelect(event: ListboxItemEmits['select'][0]) {
  if (isDisabled.value) {
    event.preventDefault()
    return
  }
  emits('select', event)
}
</script>

<template>
  <ListboxItem
    v-bind="forwarded"
    :key="selected ? 'selected' : 'unselected'"
    :aria-disabled="isDisabled ? 'true' : undefined"
    :data-selected="selected ? '' : undefined"
    :class="
      cn(
        'group relative flex w-full cursor-pointer select-none items-center gap-2 rounded-md py-1.5 pl-1 pr-9 text-sm text-gray-700 outline-hidden data-highlighted:bg-gray-100 data-highlighted:text-gray-900 data-[state=checked]:bg-gray-50 data-selected:bg-gray-50 data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:size-5 [&_svg]:shrink-0',
        props.class,
      )
    "
    @select="onSelect"
  >
    <slot />
    <span
      class="absolute right-2 hidden items-center justify-center group-data-[state=checked]:flex group-data-selected:flex"
    >
      <CheckCircleIcon class="text-green-500" />
    </span>
  </ListboxItem>
</template>
